import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { withStorageSync } from '@angular-architects/ngrx-toolkit';
import { initialStatus, LoadingStatus } from '@bcef/ia-shared';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { debounceTime, pipe, switchMap, tap } from 'rxjs';

import { ErrorStore } from '@shared/store/error.store';

import { PfuRawForm } from '../interfaces/pfu-raw-form.interface';
import { PfuRequest } from '../interfaces/pfu-request.interface';
import { PfuResponse } from '../interfaces/pfu-response.interface';
import { PfuService } from '../services/pfu.service';

export interface PfuState {
  pfuFormData: PfuRawForm;
  pfuRequest: Partial<PfuRequest>;
  pfuResponse: PfuResponse;
  loadingStatus: LoadingStatus;
}

export const initialState: PfuState = {
  pfuFormData: {
    movableCapital: {
      dividends: null,
      interests: null,
      lifeInsurance: null,
      attendanceTokens: null,
    },
    capitalGains: {
      commonLawRegime: { firstBracket: null, secondBracket: null },
      smallBusinessRegime: { firstBracket: null, secondBracket: null },
      retirementRegime: null,
    },
    relationshipSituation: null,
    incomeYear: null,
    otherIncomeOrTaxes: {
      completeNetIncome: null,
      displayDetails: false,
      details: {
        incomes: {
          wages: { declaredPerson1: null, declaredPerson2: null },
          pensions: { declaredPerson1: null, declaredPerson2: null },
          professionalBncBicBa: { declaredPerson1: null, declaredPerson2: null },
          nonProfessionalBncBicBa: { declaredPerson1: null, declaredPerson2: null },
          landIncome: null,
          realEstateCapitalGain: null,
          otherIncomeUnderFlatTax: null,
          otherIncomeUnderIncomeTax: null,
        },
        taxes: { deductibleCSG: null, alimony: null, paymentsPerp: null, otherTaxes: null },
      },
    },
    personalSituation: { comparePFUToProgressive: false, nbChildren: null, tmiNextYear: null },
    smoothingEffect: {
      computeSmoothingEffect: false,
      previousYear: null,
      secondToPreviousYear: null,
    },
  },
  pfuRequest: {},
  pfuResponse: {
    eligiblePfuIncome: 0,
    flatTax: 0,
    netGainReceived: 0,
    taxResult: [],
    suggestions: [],
  },
  loadingStatus: initialStatus,
};

export const PFU_FORM_DEBOUNCE_MILLIS = 300;

export const PfuStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withStorageSync({ key: 'ia-simulateur-epargne:pfu', storage: () => sessionStorage }),
  withMethods(
    (
      store,
      pfuService = inject(PfuService),
      errorStore = inject(ErrorStore),
      router = inject(Router),
    ) => ({
      sendPfuForm: rxMethod<void>(
        pipe(
          tap(() =>
            patchState(store, (state) => ({
              loadingStatus: { ...state.loadingStatus, isLoading: true },
            })),
          ),
          debounceTime(PFU_FORM_DEBOUNCE_MILLIS),
          switchMap(() =>
            pfuService.postPfuSimulation(store.pfuRequest()).pipe(
              tapResponse({
                next: (pfuResponse) => {
                  patchState(store, (state) => ({
                    pfuResponse,
                    loadingStatus: { ...state.loadingStatus, isLoaded: true },
                  }));
                  router.navigate(['pfu', 'summary']);
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setError(error);
                  patchState(store, (state) => ({
                    loadingStatus: { ...state.loadingStatus, error },
                  }));
                  router.navigate(['erreur']);
                },
                finalize: () =>
                  patchState(store, (state) => ({
                    loadingStatus: { ...state.loadingStatus, isLoading: false },
                  })),
              }),
            ),
          ),
        ),
      ),
      addFormData(pfuFormData: PfuRawForm): void {
        patchState(store, { pfuFormData });
      },
      addRequestData(pfuRequest: Partial<PfuRequest>): void {
        patchState(store, { pfuRequest });
      },
    }),
  ),
);
export type IPfuStore = InstanceType<typeof PfuStore>;
